type LabsLogsModule = typeof import('@/app/providers/labs/logs')
type RfqObservabilityModule = typeof import(
  '@/composables/rfq/helpers/rfq/observability'
)

const RFQ_OBSERVABILITY_RETRY_DELAY_MS = 1000

export default defineNuxtPlugin({
  name: 'labs-logs',
  dependsOn: ['store'],
  setup() {
    const sharedWalletStore = useSharedWalletStore()
    let activeWalletAddress = ''
    let activeWalletSyncToken = 0
    let labsLogsModulePromise: undefined | Promise<LabsLogsModule>
    let rfqObservabilityModulePromise:
      | undefined
      | Promise<RfqObservabilityModule>
    let unsubscribeRfqObservability:
      | undefined
      | ReturnType<RfqObservabilityModule['observability']['subscribe']>
    let rfqObservabilitySubscriptionToken = 0
    let rfqObservabilityRetryTimeout:
      | undefined
      | ReturnType<typeof setTimeout>

    function getLabsLogsStorage() {
      return typeof localStorage === 'undefined' ? undefined : localStorage
    }

    function getLabsLogsModule() {
      if (!labsLogsModulePromise) {
        labsLogsModulePromise = import('@/app/providers/labs/logs').catch(
          (error) => {
            labsLogsModulePromise = undefined

            throw error
          }
        )
      }

      return labsLogsModulePromise
    }

    function getRfqObservabilityModule() {
      if (!rfqObservabilityModulePromise) {
        rfqObservabilityModulePromise = import(
          '@/composables/rfq/helpers/rfq/observability'
        ).catch((error) => {
          rfqObservabilityModulePromise = undefined

          throw error
        })
      }

      return rfqObservabilityModulePromise
    }

    function clearRfqObservabilityRetry() {
      if (!rfqObservabilityRetryTimeout) {
        return
      }

      clearTimeout(rfqObservabilityRetryTimeout)
      rfqObservabilityRetryTimeout = undefined
    }

    function clearRfqObservabilitySubscription() {
      unsubscribeRfqObservability?.()
      unsubscribeRfqObservability = undefined
      rfqObservabilitySubscriptionToken++
    }

    function scheduleRfqObservabilityRetry() {
      if (!activeWalletAddress) {
        return
      }

      clearRfqObservabilityRetry()
      const retryWalletAddress = activeWalletAddress

      rfqObservabilityRetryTimeout = setTimeout(() => {
        rfqObservabilityRetryTimeout = undefined

        if (retryWalletAddress !== activeWalletAddress) {
          return
        }

        syncActiveLabsLogsWallet(retryWalletAddress).catch(() => undefined)
      }, RFQ_OBSERVABILITY_RETRY_DELAY_MS)
    }

    async function syncActiveLabsLogsWallet(walletAddress: string) {
      const syncToken = ++activeWalletSyncToken
      const previousWalletAddress = activeWalletAddress

      activeWalletAddress = walletAddress

      if (walletAddress && walletAddress !== previousWalletAddress) {
        clearRfqObservabilityRetry()
        clearRfqObservabilitySubscription()
      }

      try {
        if (!walletAddress) {
          clearRfqObservabilityRetry()
          clearRfqObservabilitySubscription()

          if (labsLogsModulePromise) {
            const labsLogsModule = await getLabsLogsModule()

            if (syncToken !== activeWalletSyncToken) {
              return
            }

            labsLogsModule.setActiveLabsLogsWallet({
              walletAddress,
              storage: getLabsLogsStorage()
            })
          }

          return
        }

        const labsLogsModule = await getLabsLogsModule()

        if (syncToken !== activeWalletSyncToken) {
          return
        }

        labsLogsModule.setActiveLabsLogsWallet({
          walletAddress,
          storage: getLabsLogsStorage()
        })

        subscribeRfqObservability().catch(() => undefined)
      } catch (error) {
        if (syncToken === activeWalletSyncToken) {
          scheduleRfqObservabilityRetry()
        }

        throw error
      }
    }

    async function subscribeRfqObservability() {
      if (unsubscribeRfqObservability) {
        return
      }

      const subscriptionToken = ++rfqObservabilitySubscriptionToken

      unsubscribeRfqObservability = () => undefined

      let labsLogsModule: LabsLogsModule
      let rfqObservabilityModule: RfqObservabilityModule

      try {
        const modules = await Promise.all([
          getLabsLogsModule(),
          getRfqObservabilityModule()
        ])

        labsLogsModule = modules[0]
        rfqObservabilityModule = modules[1]
      } catch (error) {
        if (subscriptionToken === rfqObservabilitySubscriptionToken) {
          unsubscribeRfqObservability = undefined
          scheduleRfqObservabilityRetry()
        }

        throw error
      }

      if (subscriptionToken !== rfqObservabilitySubscriptionToken) {
        return
      }

      if (!activeWalletAddress) {
        unsubscribeRfqObservability = undefined

        return
      }

      clearRfqObservabilityRetry()
      try {
        unsubscribeRfqObservability =
          rfqObservabilityModule.observability.subscribe((event) => {
            if (!activeWalletAddress) {
              return
            }

            const entry = labsLogsModule.createLabsRfqLogEntry(event)

            labsLogsModule.upsertLabsLogEntry({
              entry,
              storage: getLabsLogsStorage(),
              walletAddress: activeWalletAddress
            })
          })
      } catch (error) {
        if (subscriptionToken === rfqObservabilitySubscriptionToken) {
          unsubscribeRfqObservability = undefined
          scheduleRfqObservabilityRetry()
        }

        throw error
      }
    }

    watch(
      () => sharedWalletStore.injectiveAddress,
      (walletAddress) => {
        syncActiveLabsLogsWallet(walletAddress).catch(() => undefined)
      },
      { immediate: true }
    )
  }
})
